import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { Layout } from "../components/Layout";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, amount: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null });
      return;
    }
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        if (cancelled) return;
        const ps = r.data.payment_status;
        const st = r.data.status;
        setState({ status: st, payment_status: ps, amount: r.data.amount_total, currency: r.data.currency });
        if (ps === "paid" || st === "expired") return; // done
        attempts.current += 1;
        if (attempts.current < MAX_ATTEMPTS) {
          setTimeout(poll, POLL_INTERVAL);
        }
      } catch (e) {
        if (!cancelled) setState({ status: "error", payment_status: null });
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isPaid = state.payment_status === "paid";
  const isFailed = state.status === "expired" || state.status === "error";
  const isChecking = !isPaid && !isFailed;

  return (
    <Layout>
      <div data-testid="billing-success-page" className="max-w-xl mx-auto py-12">
        <div className="bg-white border border-[#E8E6E1] rounded-xl p-10 text-center">
          {isChecking && (
            <>
              <Loader2 className="w-12 h-12 text-[#2E5C50] animate-spin mx-auto" />
              <h1 data-testid="success-checking-heading" className="mt-6 font-heading text-2xl text-[#1C211F]">
                Confirming your payment…
              </h1>
              <p className="mt-2 text-sm text-[#57605A]">
                This usually takes a couple seconds.
              </p>
            </>
          )}
          {isPaid && (
            <>
              <div className="w-14 h-14 bg-[#E6F0EB] rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-[#2E5C50]" strokeWidth={1.5} />
              </div>
              <h1 data-testid="success-paid-heading" className="mt-6 font-heading text-3xl text-[#1C211F]">
                You are all set 🎉
              </h1>
              <p className="mt-3 text-sm text-[#57605A]">
                Welcome to Nexari. Your subscription is active and all features are unlocked.
              </p>
              {state.amount != null && (
                <p data-testid="success-amount" className="mt-2 text-xs text-[#57605A]">
                  Charged ${(state.amount / 100).toFixed(2)} {(state.currency || "usd").toUpperCase()}
                </p>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/dashboard"
                  data-testid="go-to-dashboard-btn"
                  className="bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
                >
                  Go to dashboard
                </Link>
                <Link
                  to="/billing"
                  data-testid="view-billing-btn"
                  className="bg-[#F9F7F4] text-[#1C211F] hover:bg-[#E8E6E1] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium border border-[#E8E6E1]"
                >
                  View billing
                </Link>
              </div>
            </>
          )}
          {isFailed && (
            <>
              <div className="w-14 h-14 bg-[#FCE8E4] rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-[#B54D40]" strokeWidth={1.5} />
              </div>
              <h1 data-testid="success-failed-heading" className="mt-6 font-heading text-2xl text-[#1C211F]">
                Payment not completed
              </h1>
              <p className="mt-3 text-sm text-[#57605A]">
                We could not confirm your payment. If you completed checkout, refresh in a few seconds — otherwise try again.
              </p>
              <Link
                to="/pricing"
                data-testid="back-to-pricing-btn"
                className="mt-6 inline-block bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
              >
                Back to plans
              </Link>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
